/* eslint-disable jsx-a11y/anchor-is-valid */
import React from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import FormControl from '@mui/material/FormControl';
import Paper from '@mui/material/Paper';
import FormLabel from '@mui/material/FormLabel';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import { TwitterPicker } from 'react-color';
import EventNoteIcon from '@mui/icons-material/EventNote';
import GroupIcon from '@mui/icons-material/Group';
import LocalOffer from '@mui/icons-material/LocalOffer';
import LocationOn from '@mui/icons-material/LocationOn';
import Info from '@mui/icons-material/Info';
import { Today } from '@mui/icons-material';
import colors from '../../../../../utils/colors';
import {
  grey,
  primary,
} from '../../../../../utils/brandColors';
import { getBarnUsersForAssignmentString } from '../../../../../utils/scheduleHelper';
import HorseIcon from '../../../../CustomIcons/Horse';
import BarnUsersAutocomplete from '../../../../Autocompletes/BarnUsers';
import BarnHorsesAutocomplete from '../../../../Autocompletes/BarnHorses';
import LocationsAutocomplete from '../../../../Autocompletes/Locations';
import TagsAutocomplete from '../../../../Autocompletes/Tags';
import ParticipantListComponent from '../../../ParticipantList';
import TagList from '../../../../TagList';

const HeaderSection = ({
  schedule,
  name,
  assignment,
  setAssignment,
  validation,
  setName,
  description,
  setDescription,
  metadata,
  setMetadata,
  location,
  barnHorses,
  handleChangeLocation,
  handleChangeBarnHorses,
  additionalTags,
  setAdditionalTags,
  detailedLocation,
  setDetailedLocation,
  open,
  selectedBarn,
  disabledForBeingInPast,
}) => {
  const [displayColorPicker, setDisplayColorPicker] = React.useState(false);
  const [addDescription, setAddDescription] = React.useState(false);
  const [addLocation, setAddLocation] = React.useState(false);
  const [changeTags, setChangeTags] = React.useState(false);
  const [changeHorses, setChangeHorses] = React.useState(false);

  const { barnHorses: boardBarnHorses } = useSelector((state) => state.board);
  const barnUsersMap = useSelector((state) => state.barn.barnUsersMap);

  React.useEffect(() => {
    setChangeTags(false);
    setChangeHorses(false);
    if (description && description.length > 0) {
      setAddDescription(true);
    } else {
      setAddDescription(false);
    }
    if (detailedLocation && detailedLocation.length > 0) {
      setAddLocation(true);
    } else {
      setAddLocation(false);
    }
  }, [name]);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Grid container>
          <Grid item xs={12} align="right">
            <ListItem dense>
              <ListItemIcon><EventNoteIcon /></ListItemIcon>
              <ListItemText>
                <TextField
                  sx={{ minWidth: '100%' }}
                  size="small"
                  variant="outlined"
                  id="Name"
                  label="Event Display Name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={disabledForBeingInPast}
                />
              </ListItemText>
              <FormControl
                sx={{ ml: 1 }}
                margin="normal"
                variant="outlined"
              >
                <FormLabel id="board-display-radio-buttons-group-label">
                  <Box
                    sx={{
                      display: 'flex',
                      cursor: 'pointer',
                    }}
                  >
                    <Box onClick={() => setDisplayColorPicker(true)}>Color</Box>
                    <div
                      className="circle"
                      style={{
                        marginLeft: '0.5rem',
                        height: '25px',
                        width: '25px',
                        borderRadius: '25px',
                        cursor: 'pointer',
                        backgroundColor:
                          (metadata && metadata.calendarDisplayColor) || primary.main,
                      }}
                      role="presentation"
                      onClick={() => setDisplayColorPicker(true)}
                    />
                    {
                      displayColorPicker
                      && (
                        <div
                          style={{
                            position: 'absolute',
                            right: 30,
                            top: 0,
                            zIndex: 1000,
                          }}
                        >
                          <div
                            style={{
                              position: 'fixed',
                              top: 0,
                              right: 0,
                              bottom: 0,
                              left: 0,
                            }}
                            onClick={() => setDisplayColorPicker(false)}
                            onKeyDown={() => setDisplayColorPicker(false)}
                            role="presentation"
                          />
                          <Paper>
                            <TwitterPicker
                              colors={colors}
                              color={(metadata && metadata.calendarDisplayColor) || primary.main}
                              onChange={(e) => {
                                const calendarDisplayColor = e.hex;
                                setMetadata({
                                  ...metadata,
                                  calendarDisplayColor,
                                });
                                if (e.hex.length === 6) {
                                  setDisplayColorPicker(false);
                                }
                              }}
                              direction="vertical"
                            />
                            <Box
                              sx={{
                                mx: 2,
                                pb: 1,
                                fontSize: 14,
                                fontWeight: 300,
                                zIndex: 10000,
                                position: 'relative',
                              }}
                            >
                              <Typography fontWeight={300} component="div">Pick your own color on the</Typography>
                              <a href="https://g.co/kgs/rxPsQ5R" target="blank">Google Hex Color Picker</a>
                            </Box>
                          </Paper>
                        </div>
                      )
                    }
                  </Box>
                </FormLabel>
              </FormControl>
            </ListItem>
          </Grid>
          <Grid item xs={12}>
            <ListItem dense>
              <ListItemIcon><Info /></ListItemIcon>
              <ListItemText>
                {
                  addDescription
                    ? (
                      <TextField
                        sx={{ minWidth: '100%' }}
                        size="small"
                        variant="outlined"
                        id="description"
                        label="Event Details"
                        value={description}
                        multiline
                        rows="3"
                        onChange={(e) => setDescription(e.target.value)}
                        disabled={disabledForBeingInPast}
                      />
                    )
                    : (
                      <Link
                        component="button"
                        variant="body2"
                        color="secondary"
                        underline="hover"
                        onClick={() => setAddDescription(true)}
                      >
                        Add Event Details
                      </Link>
                    )
                }
              </ListItemText>
            </ListItem>
          </Grid>
          <Grid item xs={12}>
            <ListItem dense>
              <ListItemIcon><LocalOffer /></ListItemIcon>
              <ListItemText>
                {
                  changeTags
                    ? (
                      <TagsAutocomplete
                        onSelect={(e, value) => { setAdditionalTags(value); }}
                        value={additionalTags}
                        size="small"
                        disabled={disabledForBeingInPast}
                      />
                    )
                    : (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        <TagList tags={additionalTags} disableGutters />
                        <Link
                          component="button"
                          variant="body2"
                          color="secondary"
                          underline="hover"
                          onClick={() => setChangeTags(true)}
                          sx={{ ml: 2 }}
                        >
                          Modify Tags
                        </Link>
                      </Box>
                    )
                }
              </ListItemText>
            </ListItem>
          </Grid>
          <Grid item xs={12}>
            <ListItem
              dense
              sx={{ marginBottom: 1 }}
            >
              <ListItemIcon><LocationOn /></ListItemIcon>
              {
                addLocation
                  ? (
                    <Grid container spacing={1} alignItems="center">
                      <Grid item xs={6}>
                        <LocationsAutocomplete
                          multiple={false}
                          validation={validation.location}
                          value={location}
                          onSelect={(e, newLocations) => {
                            handleChangeLocation(newLocations)
                          }}
                          size="small"
                          limitTags={5}
                          disabled={disabledForBeingInPast}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          size="small"
                          variant="outlined"
                          id="detailed-location"
                          label="Location Details"
                          value={detailedLocation || ''}
                          onChange={(e) => setDetailedLocation(e.target.value)}
                          disabled={disabledForBeingInPast}
                        />
                      </Grid>
                    </Grid>
                  )
                  : (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      {`${(location && location.name) || ''} ${detailedLocation
                        ? `(${detailedLocation})`
                        : ''}`}
                      <Link
                        component="button"
                        variant="body2"
                        color="secondary"
                        underline="hover"
                        onClick={() => setAddLocation(true)}
                        sx={{ ml: 2 }}
                      >
                        Change Location
                      </Link>
                    </Box>
                  )
              }
            </ListItem>
          </Grid>
          <Grid item xs={12}>
            <ListItem dense sx={{ marginBottom: 1 }}>
              <ListItemIcon><HorseIcon /></ListItemIcon>
              {
                changeHorses
                  ? (
                    <BarnHorsesAutocomplete
                      multiple
                      value={barnHorses}
                      onSelect={(e, newHorses) => {
                        handleChangeBarnHorses(newHorses)
                      }}
                      limitTags={5}
                      barnHorses={boardBarnHorses && boardBarnHorses.length > 0
                        ? boardBarnHorses
                        : undefined}
                      disabled={disabledForBeingInPast}
                    />
                  )
                  : (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      {
                        barnHorses.length > 0 && (
                          <TagList
                            tags={barnHorses.map((bh) => ({
                              id: parseInt(bh.pingumTagId, 10),
                              name: bh.horse.name,
                              tagType: { id: selectedBarn.barnMetadata.horseTagTypeId },
                            }))}
                          />
                        )
                      }
                      <Link
                        component="button"
                        variant="body2"
                        color="secondary"
                        underline="hover"
                        onClick={() => setChangeHorses(true)}
                        sx={{
                          ml: barnHorses.length > 0
                            ? 2
                            : 0,
                          mb: 0.5,
                        }}
                      >
                        Modify Horses
                      </Link>
                    </Box>
                  )
              }
            </ListItem>
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <Grid
          container
          sx={{
            backgroundColor: grey[50],
            height: '100%',
          }}
        >
          <Grid item xs={12}>
            <ListItem
              dense
            >
              <ListItemIcon>
                <Today />
              </ListItemIcon>
              <Box>
                <Typography>
                  Calendar Invitation
                </Typography>
                <Typography
                  variant="caption"
                  component="div"
                  sx={{
                    fontWeight: 300,
                    textWrap: 'auto',
                  }}
                >
                  Participants are emailed an invite. Questri records accept and decline responses.
                </Typography>
              </Box>
            </ListItem>

            <ListItem dense sx={{ alignItems: 'flex-start' }}>
              <ListItemIcon sx={{ mt: '12px' }}><GroupIcon /></ListItemIcon>
              <Box sx={{ width: '100%' }}>
                <BarnUsersAutocomplete
                  label="Participants"
                  id="barn-users-select-autocomplete-schedule-assignment"
                  multiple
                  validation={validation.assignment}
                  value={getBarnUsersForAssignmentString(assignment || '', barnUsersMap)}
                  onSelect={(e, newBarnUsers) => {
                    // take selected barn users and map to assignment syntax @[NAME](AUTH0_USER_ID)
                    setAssignment(newBarnUsers.map((bu) => (bu.id
                      ? `@[${bu.userFullName}](${bu.auth0UserId || bu.email})`
                      : `@[${bu.userFullName || bu}](${null})`)).join(''));
                  }}
                  limitTags={(schedule && schedule.id)
                    ? 2
                    : 5}
                  disabled={disabledForBeingInPast}
                />
                {
                  schedule && schedule.id && open && (
                    <Box sx={{ marginTop: 1 }}>
                      <ParticipantListComponent
                        participants={getBarnUsersForAssignmentString(assignment || '', barnUsersMap)}
                        schedule={schedule}
                      />
                    </Box>
                  )
                }
              </Box>
            </ListItem>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

HeaderSection.propTypes = {
  schedule: PropTypes.object,
  assignment: PropTypes.string,
  setAssignment: PropTypes.func.isRequired,
  name: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  validation: PropTypes.object.isRequired,
  setName: PropTypes.func.isRequired,
  setDescription: PropTypes.func.isRequired,
  metadata: PropTypes.any.isRequired,
  setMetadata: PropTypes.func.isRequired,
  location: PropTypes.object,
  barnHorses: PropTypes.any.isRequired,
  handleChangeLocation: PropTypes.func.isRequired,
  handleChangeBarnHorses: PropTypes.func.isRequired,
  additionalTags: PropTypes.array.isRequired,
  setAdditionalTags: PropTypes.func.isRequired,
  setDetailedLocation: PropTypes.func.isRequired,
  detailedLocation: PropTypes.string,
  open: PropTypes.bool.isRequired,
  selectedBarn: PropTypes.object.isRequired,
  disabledForBeingInPast: PropTypes.bool,
};

HeaderSection.defaultProps = {
  schedule: null,
  assignment: '',
  location: null,
  detailedLocation: null,
  disabledForBeingInPast: false,
};

export default HeaderSection;
